import {
  ComponentRef,
  Injectable,
  Type,
  ViewContainerRef,
  ApplicationRef,
  DOCUMENT,
  inject,
} from "@angular/core";

import { Observable, Subscriber } from "rxjs";
import { MessageService } from "primeng/api";

import { environment } from "../../environments/environment";
import { AlertComponent } from "./alert/alert.component";
import { ConfirmComponent } from "./confirm/confirm.component";
import { TOAST } from "./toast/toast-type.enum";
import { ModalAbstractComponent } from "./modal-abstract.component";
import { toastKey } from "./toast-key.const";

export interface ConfirmOptions {
  showDeleteInput?: boolean;
  destructive?: boolean;
  confirmLabel?: string;
  denyLabel?: string;
}

@Injectable()
export class NotifyService {
  private applicationRef = inject(ApplicationRef);
  private messageService = inject(MessageService);
  private document = inject<Document>(DOCUMENT);

  private registeredModals: Array<ComponentRef<any>> = [];

  public alert(message: string, title: string) {
    const componentRef: ComponentRef<AlertComponent> =
      this.createComponent<AlertComponent>(
        AlertComponent,
        this.getRootViewContainerRef(),
      );
    componentRef.instance.setMessage(message);
    componentRef.instance.setTitle(title);

    return this.watchModal<AlertComponent, void>(componentRef);
  }

  public confirm(message: string, title: string, options: ConfirmOptions = {}) {
    const componentRef: ComponentRef<ConfirmComponent> =
      this.createComponent<ConfirmComponent>(
        ConfirmComponent,
        this.getRootViewContainerRef(),
      );
    componentRef.instance.setMessage(message);
    componentRef.instance.setTitle(title);
    componentRef.instance.setShowDeleteInput(options.showDeleteInput ?? false);
    componentRef.instance.setDestructive(options.destructive ?? false);
    componentRef.instance.setLabels(options.confirmLabel, options.denyLabel);

    return this.watchModal<ConfirmComponent, void>(componentRef);
  }

  public toast(
    type: TOAST,
    title: string,
    details?: any,
    sticky = false,
    key = toastKey.default,
  ): void {
    if (environment.production && type === TOAST.SUCCESS) {
      return;
    }

    this.messageService.add({
      key: key,
      sticky: sticky,
      severity: type,
      summary: title,
      detail: details,
    });
  }

  public clear(key: string): void {
    this.messageService.clear(key);
  }

  private watchModal<T extends ModalAbstractComponent<U>, U>(
    componentRef: ComponentRef<T>,
  ): Observable<U> {
    this.document.body.classList.add("modal-open");
    componentRef.instance.closeModal.subscribe((result: U) => {
      componentRef.destroy();
    });
    componentRef.instance.dismissModal.subscribe(() => {
      componentRef.destroy();
    });
    this.registeredModals.push(componentRef);

    componentRef.onDestroy(() => {
      this.registeredModals = this.registeredModals.filter(
        (registeredModal) => registeredModal !== componentRef,
      );
      this.document.body.classList.remove("modal-open");
    });

    return new Observable<U>((observer: Subscriber<U>): void => {
      componentRef.instance.closeModal.subscribe((result: U) => {
        observer.next(result);
        observer.complete();
      });

      componentRef.instance.dismissModal.subscribe(() => {
        observer.complete();
      });
    });
  }

  private createComponent<T>(
    component: Type<T>,
    target: ViewContainerRef,
  ): ComponentRef<T> {
    return target.createComponent<T>(component);
  }

  private getRootViewContainerRef(): ViewContainerRef {
    const appInstance = this.applicationRef.components[0].instance;

    if (!appInstance.viewContainerRef) {
      const appName = this.applicationRef.componentTypes[0].name;
      throw new Error(
        `Missing 'viewContainerRef' declaration in ${appName} constructor`,
      );
    }

    return appInstance.viewContainerRef;
  }
}
